/**
 * ProjectContext - scoped project reference and storage provider management.
 * Maintains current project path and provides utilities for temporary context switching.
 */

import { DefaultStorageProvider } from './defaultStorageProvider.js';
import { AssetPath } from './storageProvider.js';

import type { IStorageProvider } from './storageProvider.js';



interface ProjectContextOptions {
  project: string | null;
  storageProvider: IStorageProvider | null;
}

class ProjectInfo {
  _project: string | null = null;
  _storageProvider: IStorageProvider | null = null;
  _defaultStorageProvider: IStorageProvider | null = null;

  setProject({project, storageProvider}: ProjectContextOptions) {
    this._project = project;
    this._storageProvider = storageProvider;
  }

  resetProject() {
    this._project = null;
    this._storageProvider = null;
  }

  get project() {
    return this._project;
  }

  get storageProvider() {
    if (this._storageProvider) {
      return this._storageProvider;
    }

    this._defaultStorageProvider ??= new DefaultStorageProvider();
    return this._defaultStorageProvider;
  }
}

export const projectInfo = new ProjectInfo();


export function projectContext({project, storageProvider}: ProjectContextOptions): () => void {
  if (projectInfo.project) {
    throw new Error(`Already under project context: ${projectInfo.project}, and trying to set it to ${project}`);
  }

  console.log(`using project context: ${project}`);
  projectInfo.setProject({project, storageProvider});

  return () => {
    console.log(`Resetting project context from: ${project}`);
    projectInfo.resetProject();
  };
}


/**
 * Creates a scoped project context disposable.
 *
 * @param project - The name of the project context
 * @param storageProvider - The storage provider to use
 * @returns A disposable that cleans up the context on dispose
 *
 * @example
 * ```ts
 * {
 *   using context = scopedProjectContext({ project: 'games/my-project', storageProvider });
 *   // do something that requires the project context
 * }
 * ```
 */
export function scopedProjectContext({project, storageProvider}: ProjectContextOptions): Disposable {
  const cleanup = projectContext({project, storageProvider});
  return {
    [Symbol.dispose]() {
      cleanup();
    }
  };
}

/**
 * Executes a function within a project context (sync).
 *
 * @param func - The function to execute
 * @param project - The name of the project context
 * @param storageProvider - The storage provider to use
 * @returns The result of the function
 *
 * @example
 * ```ts
 * usingProjectContext(() => {
 *   // do something that requires the project context
 * }, { project: 'games/my-project', storageProvider });
 * ```
 */
export function usingProjectContext<T>(func: () => T, {project, storageProvider}: ProjectContextOptions): T {
  const cleanup = projectContext({project, storageProvider});
  try {
    return func();
  } finally {
    cleanup();
  }
}

/**
 * Executes a function within a project context (async).
 *
 * @param func - The async function to execute
 * @param project - The name of the project context
 * @param storageProvider - The storage provider to use
 * @returns The result of the function
 *
 * @example
 * ```ts
 * await usingProjectContextAsync(async () => {
 *   // do something that requires the project context
 * }, { project: 'games/my-project', storageProvider });
 * ```
 */
export async function usingProjectContextAsync<T>(func: () => Promise<T>, {project, storageProvider}: ProjectContextOptions): Promise<T> {
  const cleanup = projectContext({project, storageProvider});
  try {
    return await func();
  } finally {
    cleanup();
  }
}

/**
 * Resolves a path to a fetch compatible URL using the project's storage provider.
 *
 * @param path - The path to resolve
 * @returns The resolved path
 */
export async function resolvePath(path: AssetPath): Promise<AssetPath> {
  const storageProvider = projectInfo.storageProvider;
  if (!storageProvider) {
    throw new Error('No storage provider found');
  }
  return await storageProvider.resolvePath(path);
}

/**
 * Resolves all @engine/ or @project/ paths in the text to their resolved paths.
 *
 * @param text - The text to resolve
 * @returns The resolved text
 */
export async function resolveAssetPathsInText(text: string): Promise<string> {
  // Regular expression to find @engine/ or @project/ paths in the text
  // Matches paths in quotes, src attributes, or other contexts
  const assetPathRegex = /(@(?:engine|project)\/[^"\s'<>]+)/g;

  let resolvedContent = text;
  const matches = [...text.matchAll(assetPathRegex)];

  if (matches.length > 0) {
    // Process matches in reverse order to avoid index shifting issues
    for (let i = matches.length - 1; i >= 0; i--) {
      const match = matches[i];
      const fullMatch = match[0];
      const assetPath = match[1];

      const resolvedPath = await resolvePath(AssetPath.fromString(assetPath));

      // Replace the matched path with the resolved path
      resolvedContent = resolvedContent.replace(fullMatch, resolvedPath.getResolvedPath());
    }
  }

  return resolvedContent;
}
